import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import {
  getRequirementId,
  isRequirementRef,
  toRequirementRef,
} from '../../../../../util/customAuthenticationFactorRequirement.util'
import { INPUT_POLICY_PATH, isCustomAuthenticationFactorInput } from '../../policy_feature/inputs'
import useNameFieldRequirements, {
  buildNameRequirementOptionsMap,
  getAutoModeNameInputs,
} from '../NameRequirements/useNameFieldRequirements'

// A custom authentication factor requirement is just the name matched in the target tenant.
const CUSTOM_AUTHENTICATION_FACTOR_REFS = {
  isInput: isCustomAuthenticationFactorInput,
  isRequirementRef,
  getRequirementId,
  toRequirementRef,
}

// Every value on this field is a factor name, whether stored raw or as "customFactor:<name>".
const toFactorNames = (values) => [
  ...new Set((values || []).map((value) => getRequirementId(value) ?? String(value ?? '').trim()).filter(Boolean)),
]

export const getAutoModeCustomAuthenticationFactorInputs = (policyBody) =>
  getAutoModeNameInputs(policyBody, CUSTOM_AUTHENTICATION_FACTOR_REFS)

export const buildCustomAuthenticationFactorRequirementOptionsMap = (values) =>
  buildNameRequirementOptionsMap(values, CUSTOM_AUTHENTICATION_FACTOR_REFS)

// Unlike the shared name hook (whose other fields keep built-in selectors alongside requirements),
// nothing on this field is anything but a factor name. So the attached names are always the whole
// field: entering automatic resolution adopts the factors already set (so the picker shows every
// factor that will be saved), and every change replaces the field rather than keeping raw values.
export default function useCustomAuthenticationFactorFieldRequirements({
  initialPolicyBody,
  policyBody,
  setPolicyBody,
}) {
  const nameFieldRequirements = useNameFieldRequirements({
    initialPolicyBody,
    setPolicyBody,
    refs: CUSTOM_AUTHENTICATION_FACTOR_REFS,
  })

  const setAttachedRequirementIds = (input, updatedIds) => {
    nameFieldRequirements.setAttachedRequirementIds(input, updatedIds)
    setPolicyBody((previousPolicyBody) => {
      const updated = cloneDeep(previousPolicyBody) || {}
      set(updated, INPUT_POLICY_PATH[input], updatedIds.map(toRequirementRef))
      return updated
    })
  }

  const toggleAutoMode = (input, enabled) => {
    nameFieldRequirements.toggleAutoMode(input, enabled)
    if (enabled) {
      setAttachedRequirementIds(input, toFactorNames(get(policyBody, INPUT_POLICY_PATH[input])))
    }
  }

  return { ...nameFieldRequirements, setAttachedRequirementIds, toggleAutoMode }
}
